// src/components/shared/StockAlignmentViewer.tsx
//
// 3D alignment viewer for the "irregular_drawing" stock flow. Renders the
// stock as a semi-transparent wireframe envelope and the part as a tinted
// solid envelope inside it, with a TransformControls gizmo for the user to
// nudge translation / rotation. Confirm button persists the placement.
//
// Per checkpoint rule: GeometryPreview's mesh-building is monolithic and not
// trivially reusable here, so this is a v1 bbox-only renderer — alignment
// really only needs the envelope to be useful.

import { useState, Suspense, useRef, useMemo } from 'react'
import { Canvas } from '@react-three/fiber'
import { OrbitControls, TransformControls } from '@react-three/drei'
import * as THREE from 'three'

export interface PartPlacement {
  position: [number, number, number]
  rotation: [number, number, number]
}

interface Props {
  stockGeometry: Record<string, unknown>
  partGeometry: Record<string, unknown>
  initialPlacement: PartPlacement
  reason?: string
  onConfirm(placement: PartPlacement): void
}

/** Pull bbox size + center out of an ExtractedGeometry blob. */
function extractBbox(geometry: Record<string, unknown>): {
  size: [number, number, number]
  center: [number, number, number]
} {
  const env = geometry.part_envelope as
    | { min_x: number; max_x: number; min_y: number; max_y: number; min_z: number; max_z: number }
    | undefined
  if (env && (env.max_x - env.min_x !== 0 || env.max_y - env.min_y !== 0 || env.max_z - env.min_z !== 0)) {
    return {
      size: [env.max_x - env.min_x, env.max_y - env.min_y, env.max_z - env.min_z],
      center: [(env.min_x + env.max_x) / 2, (env.min_y + env.max_y) / 2, (env.min_z + env.max_z) / 2],
    }
  }
  const sd = geometry.stock_dimensions as { x?: number; y?: number; z?: number } | undefined
  const sx = sd?.x ?? 50
  const sy = sd?.y ?? 50
  const sz = sd?.z ?? 50
  return { size: [sx, sy, sz], center: [0, 0, 0] }
}

function StockBboxWireframe({ geometry }: { geometry: Record<string, unknown> }) {
  const { size, center } = useMemo(() => extractBbox(geometry), [geometry])
  return (
    <group position={center}>
      <mesh>
        <boxGeometry args={size} />
        <meshBasicMaterial color="#94a3b8" wireframe transparent opacity={0.4} depthWrite={false} />
      </mesh>
    </group>
  )
}

function PartMesh({ geometry }: { geometry: Record<string, unknown> }) {
  const { size, center } = useMemo(() => extractBbox(geometry), [geometry])
  return (
    <group position={center}>
      <mesh>
        <boxGeometry args={size} />
        <meshPhongMaterial
          color="#06b6d4"
          transparent
          opacity={0.35}
          depthWrite={false}
        />
      </mesh>
      <mesh>
        <boxGeometry args={size} />
        <meshBasicMaterial color="#06b6d4" wireframe transparent opacity={0.8} />
      </mesh>
    </group>
  )
}

export default function StockAlignmentViewer({
  stockGeometry, partGeometry, initialPlacement, reason, onConfirm,
}: Props) {
  const [placement, setPlacement] = useState<PartPlacement>(initialPlacement)
  const [mode, setMode] = useState<'translate' | 'rotate'>('translate')
  const partGroupRef = useRef<THREE.Group>(null)

  // Frame the camera around the stock bbox so the gizmo is always reachable.
  const { size: stockSize } = useMemo(() => extractBbox(stockGeometry), [stockGeometry])
  const extent = Math.max(stockSize[0], stockSize[1], stockSize[2], 20)
  const camPos: [number, number, number] = [extent * 1.2, extent * 1.2, extent * 1.2]

  return (
    <div className="space-y-3">
      {/* Mode toggle */}
      <div className="flex items-center gap-2">
        <button
          type="button"
          onClick={() => setMode('translate')}
          className={`px-2 py-1 font-mono text-[10px] tracking-widest uppercase rounded-sm transition-colors ${
            mode === 'translate' ? 'bg-cyan-500 text-slate-950' : 'bg-slate-800 text-slate-400 hover:text-slate-200'
          }`}
        >
          Translate
        </button>
        <button
          type="button"
          onClick={() => setMode('rotate')}
          className={`px-2 py-1 font-mono text-[10px] tracking-widest uppercase rounded-sm transition-colors ${
            mode === 'rotate' ? 'bg-cyan-500 text-slate-950' : 'bg-slate-800 text-slate-400 hover:text-slate-200'
          }`}
        >
          Rotate
        </button>
        {reason && (
          <span className="ml-auto font-mono text-[10px] text-cyan-400/70 italic">
            {reason}
          </span>
        )}
      </div>

      {/* Viewer */}
      <div className="h-72 border border-slate-800 rounded-sm bg-slate-950/50">
        <Canvas camera={{ position: camPos, fov: 40, near: 0.1, far: extent * 50 }}>
          <ambientLight intensity={0.5} />
          <directionalLight position={[extent, extent, extent / 2]} intensity={0.8} />
          <directionalLight position={[-extent, -extent / 2, -extent / 2]} intensity={0.3} />
          <Suspense fallback={null}>
            <StockBboxWireframe geometry={stockGeometry} />
            <TransformControls
              mode={mode}
              onObjectChange={() => {
                const g = partGroupRef.current
                if (g) {
                  setPlacement({
                    position: [g.position.x, g.position.y, g.position.z],
                    rotation: [g.rotation.x, g.rotation.y, g.rotation.z],
                  })
                }
              }}
            >
              <group ref={partGroupRef} position={placement.position} rotation={placement.rotation}>
                <PartMesh geometry={partGeometry} />
              </group>
            </TransformControls>
          </Suspense>
          <OrbitControls makeDefault />
        </Canvas>
      </div>

      {/* Numeric readout */}
      <div className="font-mono text-[10px] text-slate-500">
        Position: [{placement.position.map(v => v.toFixed(1)).join(', ')}] mm
        <span className="ml-3">
          Rotation: [{placement.rotation.map(r => `${(r * 180 / Math.PI).toFixed(1)}°`).join(', ')}]
        </span>
      </div>

      {/* Confirm */}
      <button
        type="button"
        onClick={() => onConfirm(placement)}
        className="px-3 py-1.5 font-mono text-xs tracking-widest uppercase text-slate-950 bg-cyan-500 hover:bg-cyan-400 rounded-sm transition-colors"
      >
        Confirm Alignment
      </button>
    </div>
  )
}
